import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import DatasetItem from "@/components/dataset/datasetList/DatasetItem";
import { Accordion } from "@/components/ui/accordion.tsx";
import { MAX_RANKING_INPUT } from "@/constants/dataset";
import { RootState } from "@/store";
import {
  changeSelectedAttribute,
  removeDatasetCatchmentById,
  setSelectedDatasetCatchment,
  updateCatchmentDatasetStyleEnabledRank,
  updateCatchmentDatasetStyleMaxRanking,
  updateVisibilityDatasetCatchment
} from "@/store/slice/spatialSlice";
import { Dataset } from "@/types/dataset";
import { isRankableAttribute } from "@/utils/map/rank";

type DatasetListProps = {
  onOpenDatasetTabularModal?: () => void;
  isSpatialAnalysis?: boolean;
  handleChangeShowLabel: (checked: boolean, datasetId: string) => void;
};

const CatchmentDatasetList = ({
  onOpenDatasetTabularModal,
  isSpatialAnalysis = false,
  handleChangeShowLabel
}: DatasetListProps) => {
  const dispatch = useDispatch();
  const {
    catchmentSettingsTemp: { datasetConfig },
    isShowMarkerLabel
  } = useSelector((state: RootState) => state.spatial);

  const [itemFocused, setItemFocused] = useState<Dataset | null>(null);

  const onRemoveDataset = (id: string) => {
    dispatch(removeDatasetCatchmentById(id));
  };

  const orderedDatasets = datasetConfig?.visualizationOrder.map(
    (id) => datasetConfig.datasets[id].dataset
  );

  return (
    <div>
      <Accordion type="single" collapsible className="w-full">
        <ul className="flex flex-col gap-2 mt-3 overflow-auto custom-scrollbar">
          {orderedDatasets?.map((item) => {
            const totalRows =
              datasetConfig?.datasets[item.data.id]?.rawDataset?.rows?.length ||
              0;

            const maxRankingInput = Math.min(totalRows, MAX_RANKING_INPUT);
            const schema = item.data.schema;

            return (
              <DatasetItem
                key={item.data.id}
                data={item}
                isFocused={item.data.id === itemFocused?.data.id}
                isSpatialAnalysis={isSpatialAnalysis}
                titleColorBasedSelect="Select Attribute"
                isEditMode={false}
                popupOffsetId="catchment"
                onMouseEnter={() => setItemFocused(item)}
                onMouseLeave={() => setItemFocused(null)}
                onRemoveDataset={() => onRemoveDataset(item.data.id)}
                onOpenDatasetTabularModal={() => {
                  onOpenDatasetTabularModal?.();
                  dispatch(setSelectedDatasetCatchment(item));
                }}
                onToggleDatasetVisibility={(e) => {
                  e.stopPropagation();
                  dispatch(
                    updateVisibilityDatasetCatchment({
                      id: item.data.id,
                      isVisible: !item.properties.isVisible
                    })
                  );
                }}
                onSelectAttribute={(value) => {
                  const selectedAttribute = item.categories.find(
                    (category) => category.attribute_name === value
                  );
                  if (selectedAttribute) {
                    dispatch(
                      changeSelectedAttribute({
                        id: item.data.id,
                        attribute: selectedAttribute
                      })
                    );
                    dispatch(
                      updateCatchmentDatasetStyleEnabledRank({
                        datasetId: item.data.id,
                        isEnabled: isRankableAttribute(
                          schema,
                          selectedAttribute.id
                        )
                      })
                    );
                  }
                }}
                onShowLabel={(checked) =>
                  handleChangeShowLabel(checked, item.data.id)
                }
                showLabel={isShowMarkerLabel}
                totalRows={totalRows}
                handleChangeRankChecked={(value) => {
                  dispatch(
                    updateCatchmentDatasetStyleEnabledRank({
                      datasetId: item.data.id,
                      isEnabled: value
                    })
                  );
                }}
                handleChangeMaxRankingInput={(value) => {
                  if (value > maxRankingInput) value = maxRankingInput;
                  if (value < 1) value = 1;

                  dispatch(
                    updateCatchmentDatasetStyleMaxRanking({
                      datasetId: item.data.id,
                      maxRanking: value
                    })
                  );
                }}
                handleChangeMaxRankingSlider={(value) => {
                  let newValue = value[0];
                  if (newValue < 1) newValue = 1;

                  dispatch(
                    updateCatchmentDatasetStyleMaxRanking({
                      datasetId: item.data.id,
                      maxRanking: newValue
                    })
                  );
                }}
              />
            );
          })}
        </ul>
      </Accordion>
    </div>
  );
};

export default CatchmentDatasetList;
